import React, { FC, useEffect } from 'react';
import {
  Button,
  Checkbox,
  CircularProgress,
  TextField,
} from '@mui/material';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
  useGetMassTypesQuery,
  useGetProjectsQuery,
  usePostOrderMutation,
} from '../../../../core/redux/transport';
import {
  translateNBVehicleType,
  translateVehicleNumberType,
  translateWhenType,
} from '../../../../core/helpers/translate';
import { When } from '../../../../core/types/transport/when';
import { NewLoad } from '../../../../core/types/transport/api/newLoad';
import { NewOrder } from '../../../../core/types/transport/api/newOrder';
import { dateWithoutTimezone } from '../../../../shared/logic/dates';
import { InnsendingLinje } from '../../../../components/Header4';
import { PageUnauthenticated } from '../../../../shared/components/page';
import { Header } from '../../../../components/header';
import { Content } from '../../../../shared/components/Content';
import { CancelButton } from '../../../../shared/components/CancelButton';
import { resetStates } from '../../../../core/redux/massOrderState';
import { SplitOrder } from '../../../../components/SplitOrder';
import { calculateTonnage } from '../../../../core/helpers/functions';
import { useDispatch, useSelector } from '../../../../shared/hooks/redux';
import { FileLink } from '../../../../shared/components/FileLink';

export const UtInnsendingPage: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: projects } = useGetProjectsQuery();
  const { data: massTypes } = useGetMassTypesQuery();
  const {
    projectFrom,
    projectTo,
    cubic,
    typeOfMass,
    massType,
    trips,
    numberOfVehicles,
    deliveryTimeType,
    startTime,
    endTime,
    vehicleType,
    deliveryDate,
    isEarlyDelivery,
    isImportantDeadLine,
    timerCounter,
    orderComment,
    contactPerson,
    splitLoad,
    splitCubic,
    splitTypeOfMass,
    typeOfDeclaration,
    sortedAndControlled,
    meetsRequirements,
    hasDocumentationOfCleanliness,
    reasonForNoDocumentationOfCleanliness,
    uploadedFileDeclaration,
    documentationOfCleanliness,
    requiresDeclaration,
    uploadedAttachments,
  } = useSelector((s) => s.massOrderState);

  if (
    projectTo === null
    || cubic === null
    || typeOfMass === 0
    || vehicleType.type === 0
  ) {
    navigate('/bestilling');
    return null;
  }

  function getWhen(): When {
    const timeWhen = translateWhenType(deliveryTimeType.time);
    if (timeWhen === 'Custom') {
      return {
        when: timeWhen,
        from: startTime ? startTime.toISOString() : undefined,
        to: endTime ? endTime.toISOString() : undefined,
      };
    }
    return {
      when: timeWhen,
    };
  }

  const contactId: number | undefined = contactPerson?.employeeNumber || undefined;
  const contactName: string | undefined = contactPerson?.name || undefined;
  const contactNumber: string | undefined = contactPerson?.number || undefined;

  const createLoad = (): [NewLoad, NewLoad?] => {
    if (splitLoad) {
      return [
        {
          amount: cubic,
          massTypeId: typeOfMass,
        },
        {
          amount: splitCubic || 0,
          massTypeId: splitTypeOfMass,
        },
      ];
    }
    return [
      {
        amount: cubic,
        massTypeId: typeOfMass,
      },
    ];
  };

  const [sendOrder, sendOrderResponse] = usePostOrderMutation();

  const postOrder = () => {
    if (!projectTo || !deliveryDate.day) {
      throw new Error('Fant ikke fra prosjekt eller dag');
    }
    const orderToSend: NewOrder = {
      type: 'MassOut',
      // switched states as projectFrom is set at BestillingPage and is common for both mass in
      // and mass out
      // TODO: Change name from projectTo to something like requiredProject to reflect this
      fromProject: projectTo,
      toProject: projectFrom || undefined,
      loads: createLoad(),
      date: dateWithoutTimezone(deliveryDate.day),
      when: getWhen(),
      earlyDelivery: isEarlyDelivery,
      importantDeadline: isImportantDeadLine,
      vehicle: translateVehicleNumberType(vehicleType.type),
      extraTimeInFacility: timerCounter || 0,
      comment: orderComment || '',
      contactPersonEmployeeNumber: contactId,
      contactPersonName: contactName,
      contactPersonPhoneNumber: contactNumber,
      typeOfDeclaration,
      sortedAndControlled,
      meetsRequirements,
      hasDocumentationOfCleanliness,
      reasonForNoDocumentationOfCleanliness,
      uploadedFileDeclaration,
      documentationOfCleanliness,
      uploadedAttachments,
    };
    sendOrder(orderToSend);
  };

  useEffect(() => {
    if (sendOrderResponse.isSuccess) {
      navigate(`/bestilling/bekreftelse/${sendOrderResponse.data.id}`);
    }
  }, [sendOrderResponse.isSuccess]);

  useEffect(() => {
    if (sendOrderResponse.isError) {
      navigate('/masseut/innsending');
    }
  }, [sendOrderResponse.isError]);

  function checkIfImportantDeadline() {
    return <h5>Viktig Tidsfrist!</h5>;
  }

  function displayTime() {
    if (deliveryTimeType.type < 5) {
      return <div>{deliveryTimeType.time}</div>;
    }
    return (
      <div>
        <div>{`Tidligst: ${startTime && format(startTime, 'HH:mm')} `}</div>
        <div>{`Senest: ${endTime && format(endTime, 'HH:mm')} `}</div>
      </div>
    );
  }

  function displayProjectFrom() {
    const project = projects?.find((e) => e.id === projectFrom);
    return (
      <div>
        <InnsendingLinje text="Til prosjekt" />
        <div>{`${project?.id} ${project?.projectName}`}</div>
      </div>
    );
  }

  const mass = massTypes?.find((e) => e.id === `${typeOfMass}`)?.name;
  const splitMass = massTypes?.find((e) => e.id === `${splitTypeOfMass}`)?.name;
  const project = projects?.find((e) => e.id === projectTo);
  const isConcrete = mass === 'Betong';

  return (
    <PageUnauthenticated className="innsending-page parent-pos">
      <Header>Massebestilling</Header>
      <Content>
        <InnsendingLinje
          text={`Steg ${requiresDeclaration ? 5 : 4} - Innsending`}
          styling="page-item"
        />
        <div className="extra-inline-padding">
          <InnsendingLinje text="Din bestilling er klar til innsending" />
          <p>
            Les gjennom oppsummeringen under og sjekk at informasjonen stemmer
            før du sender
          </p>
          <InnsendingLinje text="Masse ut" />
          <InnsendingLinje text="Fra prosjekt" />
          <div>{`${project?.id} ${project?.projectName}`}</div>
          {projectFrom && displayProjectFrom()}
          <InnsendingLinje text="Masse type" />
          <div>{mass}</div>
          <InnsendingLinje text="Mengde" />
          <div>{`${cubic} m3`}</div>
          {splitLoad && (
            <SplitOrder splitMass={splitMass} splitCubic={splitCubic || 0} />
          )}
          <div>{`Cirka ${calculateTonnage(cubic)} tonn`}</div>
          {isConcrete && (
          <div className="info-item">
            <InnsendingLinje text="Egenerklæring" />
            <div className="info-content-column">
              {uploadedFileDeclaration && <FileLink file={uploadedFileDeclaration} />}
              {typeOfDeclaration === 'SentEarlier' && 'Erklæring er sendt tidligere'}
              {typeOfDeclaration === 'WillSendLater' && 'Erklæring vil bli sendt senere'}
              {typeOfDeclaration === 'FillOutNow' && (
              <div>
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <Checkbox checked={sortedAndControlled} sx={{ paddingLeft: 0 }} />
                  Grundig sortering og kontroll av massepartiet er foretatt
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <Checkbox checked={meetsRequirements} sx={{ paddingLeft: 0 }} />
                  <span>
                    Massen tilfredsstiller krav i prosedyre {massType?.procedureName}{' '}
                    <a
                      href={massType?.procedureUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      (Lenke til prosedyre)
                    </a>
                  </span>
                </span>
                {(documentationOfCleanliness?.length ?? 0) > 0 && (
                <div style={{ marginTop: 8 }}>
                  <p>Dokumentasjon på at massene er rene:</p>
                  <ul>
                    {documentationOfCleanliness?.map((file) => (
                      <li key={file.id}>
                        <FileLink file={file} />
                      </li>
                    ))}
                  </ul>
                </div>
                )}
                {reasonForNoDocumentationOfCleanliness && (
                <div style={{ marginTop: 8 }}>
                  <p style={{ marginBottom: 8 }}>Årsak til manglende dokumentasjon:</p>
                  <TextField
                    defaultValue={reasonForNoDocumentationOfCleanliness}
                    variant="outlined"
                    size="small"
                    multiline
                    fullWidth
                    disabled
                    sx={{
                      '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: 'black',
                      },
                    }}
                  />
                </div>
                )}
              </div>
              )}
            </div>
          </div>
          )}
          <InnsendingLinje text="Anbefalt kjøretøy" />
          <div>{translateNBVehicleType(vehicleType.type)}</div>
          <div>{`Antall turer/biler: ${trips}/${numberOfVehicles}`}</div>
          <InnsendingLinje text="Leveringsdato" />
          <div>
            {`${deliveryDate.day && format(deliveryDate.day, 'dd.MM.yyyy')}`}
          </div>
          <InnsendingLinje text="Kan leveres dagen før" />
          <div>{isEarlyDelivery ? 'Ja' : 'Nei'}</div>
          <InnsendingLinje text="Leveringstidspunkt" />
          {displayTime()}
          {isImportantDeadLine && checkIfImportantDeadline()}
          <InnsendingLinje text="Ekstra tid i anlegg" />
          <div>{`${timerCounter || 0} time(r)`}</div>
          <InnsendingLinje text="Kontaktperson" />
          <div>{`${contactPerson?.name} - ${contactPerson?.number}`}</div>
          {uploadedAttachments && uploadedAttachments.length > 0 && (
            <div>
              <InnsendingLinje text="Vedlegg" />
              {uploadedAttachments.length === 1 ? <FileLink file={uploadedAttachments[0]} />
                : (
                  <ul>
                    {uploadedAttachments.map((file) => (
                      <li key={file.id}>
                        <FileLink file={file} />
                      </li>
                    ))}
                  </ul>
                )}
            </div>
          )}
          <InnsendingLinje text="Kommentar" />
          <div>{orderComment}</div>
        </div>
        <div className="bottom-inner">
          <div className="bottom-element">
            <Button
              className="left-element"
              onClick={() => navigate(-1)}
              variant="contained"
              size="large"
            >
              Tilbake
            </Button>
            <CancelButton
              confirm={() => {
                dispatch(resetStates());
                return navigate('/bestilling');
              }}
            />
            <Button
              className="right-element"
              disabled={sendOrderResponse.isLoading}
              onClick={postOrder}
              variant="contained"
              size="large"
            >
              Send inn
            </Button>
            {sendOrderResponse.isLoading && (
              <CircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )}
          </div>
        </div>
      </Content>
    </PageUnauthenticated>
  );
};
